import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, Zap, Save, Loader2, History, ArrowRight, Sparkles, Ban, AlertTriangle, Pencil } from 'lucide-react';
import { PRIORITY_MARKETS, OTHER_MARKETS } from '../../services/marketCountries';
import AdCreativeViewer, { parseAdImages } from '../../services/AdCreativeViewer';
import AdScreenshotsField from './AdScreenshotsField';
import {
  PAID_CHANNEL_LIST,
  META_PLATFORMS,
  channelMeta,
  resolvePaidChannels,
  describeChannels
} from '../../services/paidChannels';

const basePath = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

// The generate button's range, per spec.
const GEN_MIN = 1000;
const GEN_MAX = 2000;
const randomTraffic = () => Math.floor(Math.random() * (GEN_MAX - GEN_MIN + 1)) + GEN_MIN;

const fmt = (n) => (n === null || n === undefined || n === '' ? '—' : Number(n).toLocaleString());

const fmtDate = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

/**
 * Sets affiliate_clean.adwords_traffic and writes a paid_traffic_history row in
 * one call.
 *
 * What the operator records depends on where they saw the ad. A Google find is
 * identified by the keyword that surfaced it; a Meta find has no keyword at all,
 * so it is identified by the Page that ran it plus the Ad Library ID. The form
 * swaps its middle section on the channel rather than showing both sets and
 * letting half of them sit empty.
 *
 * Both channels write the same paid traffic figure — the app has one notion of
 * paid traffic and the channel qualifies it.
 */
const SetPaidTrafficModal = ({ row, onClose, onSaved }) => {
  const [traffic, setTraffic] = useState(row.adwords_traffic ?? '');
  const [channel, setChannel] = useState('google');
  const [country, setCountry] = useState('');
  // Tracks whether the current value came from the generate button, so the
  // history row is labelled honestly even if the operator generates then saves.
  const [source, setSource] = useState('manual');

  // Google evidence.
  const [keyword, setKeyword] = useState('');

  // Meta evidence, straight off the Ad Library card.
  const [advertiserName, setAdvertiserName] = useState('');
  const [platforms, setPlatforms] = useState([]);
  const [adLibraryId, setAdLibraryId] = useState('');
  const [adStatus, setAdStatus] = useState('active');
  const [adStartedAt, setAdStartedAt] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  // Correcting an entry already in the log. Saving the main form would append a
  // NEW find, so fixing one detail — the country was Uganda, not South Africa —
  // would otherwise mean re-typing the advertiser, platforms and Library ID and
  // leaving a duplicate behind.
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ country: '', keyword: '', advertiser_name: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  // "This affiliate stopped running ads" — separate from saving a new value.
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [removeText, setRemoveText] = useState('');
  const [removing, setRemoving] = useState(false);
  // Which channel to take it off. '' means all of them.
  const [removeChannel, setRemoveChannel] = useState('');

  const isSocial = channel !== 'google';
  // The Ad Library says this campaign has stopped. Logging it records that they
  // WERE advertising — it must not add paid traffic they are no longer getting.
  const isInactiveAd = isSocial && adStatus === 'inactive';

  // Which logged find this form is aimed at. null = add a new one.
  //
  // Saving always used to append, so fixing one field on an existing find meant
  // retyping the advertiser, platforms, Library ID and date — and left a
  // duplicate behind with the mistake still in it.
  const [targetEntryId, setTargetEntryId] = useState(null);
  const isEditingEntry = targetEntryId !== null;

  // Finds on the channel currently selected — correcting a Google find from the
  // Meta form would make no sense of the fields on screen.
  const editableFinds = useMemo(
    () => history.filter((h) => h.source !== 'removal' && (h.channel || 'google') === channel),
    [history, channel]
  );

  /** Loads a logged find into the form so it can be corrected in place. */
  const loadEntryIntoForm = (entry) => {
    setTargetEntryId(entry.id);
    setError('');
    setCountry(entry.country || '');
    setKeyword(entry.keyword || '');
    setAdvertiserName(entry.advertiser_name || '');
    setPlatforms(entry.platforms ? entry.platforms.split(',').map((p) => p.trim()).filter(Boolean) : []);
    setAdLibraryId(entry.ad_library_id || '');
    setAdStatus(entry.ad_status || 'active');
    // <input type="date"> needs YYYY-MM-DD; the API returns an ISO timestamp.
    setAdStartedAt(entry.ad_started_at ? String(entry.ad_started_at).slice(0, 10) : '');
    setExistingImages(parseAdImages(entry.ad_images));
  };

  // Screenshots on the find currently loaded into the form.
  const [existingImages, setExistingImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);

  // Screenshots picked while writing a NEW find. They cannot be uploaded yet —
  // images attach to a log entry and there is no entry until the find is saved
  // — so they are held here and sent the moment the save returns an id. The
  // alternative was telling the operator to save, reopen and come back, which
  // is three steps for one thought.
  const [pendingImages, setPendingImages] = useState([]);

  const addPendingImages = (fileList, inputEl) => {
    const files = Array.from(fileList || []);
    if (files.length > 0) {
      setPendingImages((current) =>
        // previewUrl is revoked on removal and on unmount; without that each
        // pick leaks a blob for the lifetime of the page.
        [...current, ...files.map((file) => ({ file, previewUrl: URL.createObjectURL(file) }))].slice(0, 6)
      );
    }
    if (inputEl) inputEl.value = '';
  };

  const removePendingImage = (index) => {
    setPendingImages((current) => {
      const target = current[index];
      if (target) URL.revokeObjectURL(target.previewUrl);
      return current.filter((_, i) => i !== index);
    });
  };

  // Read through a ref so the unmount cleanup sees the CURRENT previews. An
  // effect closing over pendingImages directly would capture the empty array
  // from the first render and revoke nothing, while adding it as a dependency
  // would revoke the URLs on every pick.
  const pendingImagesRef = useRef(pendingImages);
  pendingImagesRef.current = pendingImages;

  useEffect(() => () => {
    pendingImagesRef.current.forEach(({ previewUrl }) => URL.revokeObjectURL(previewUrl));
  }, []);

  /** Sends the held-back screenshots once the find they belong to exists. */
  const flushPendingImages = async (entryId) => {
    if (!entryId || pendingImages.length === 0) return;

    const form = new FormData();
    pendingImages.forEach(({ file }) => form.append('images', file));

    const res = await fetch(`${basePath}/api/paid-traffic/history/${entryId}/images`, {
      method: 'POST',
      credentials: 'include',
      body: form
    });
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.message || 'The find was saved but its screenshots were not');

    pendingImages.forEach(({ previewUrl }) => URL.revokeObjectURL(previewUrl));
    setPendingImages([]);
  };

  const uploadImages = async (fileList, inputEl) => {
    const files = Array.from(fileList || []);
    if (files.length === 0 || !targetEntryId) return;

    setUploadingImages(true);
    setError('');
    try {
      const form = new FormData();
      files.forEach((f) => form.append('images', f));

      const res = await fetch(`${basePath}/api/paid-traffic/history/${targetEntryId}/images`, {
        method: 'POST',
        credentials: 'include',
        body: form // no Content-Type — the browser sets the multipart boundary
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || `Server error: ${res.status}`);

      setExistingImages(data.images || []);
      loadHistory();
    } catch (err) {
      setError(err.message || 'Failed to upload the screenshots');
    } finally {
      setUploadingImages(false);
      // Lets the same file be picked again after a failure.
      if (inputEl) inputEl.value = '';
    }
  };

  const removeImage = async (imagePath) => {
    if (!targetEntryId) return;
    setError('');
    try {
      const res = await fetch(`${basePath}/api/paid-traffic/history/${targetEntryId}/images`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: imagePath })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || `Server error: ${res.status}`);

      setExistingImages(data.images || []);
      loadHistory();
    } catch (err) {
      setError(err.message || 'Failed to remove the screenshot');
    }
  };

  /** Back to logging a brand new find. */
  const clearTarget = () => {
    setTargetEntryId(null);
    setCountry('');
    setKeyword('');
    setAdvertiserName('');
    setPlatforms([]);
    setAdLibraryId('');
    setAdStatus('active');
    setAdStartedAt('');
    setExistingImages([]);
    setError('');
  };

  // Channels currently in force on this affiliate, for the removal controls.
  const liveChannels = useMemo(
    () => resolvePaidChannels(row.paid_channels, row.adwords_traffic),
    [row.paid_channels, row.adwords_traffic]
  );

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await fetch(`${basePath}/api/paid-traffic/${row.id}/history`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setHistory(data.data || []);
    } catch {
      /* history is supplementary — a failure here must not block the edit */
    } finally {
      setHistoryLoading(false);
    }
  }, [row.id]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleGenerate = () => {
    setTraffic(String(randomTraffic()));
    setSource('generated');
  };

  const togglePlatform = (platform) => {
    setPlatforms((current) =>
      current.includes(platform) ? current.filter((p) => p !== platform) : [...current, platform]
    );
  };

  const handleSave = async () => {
    const parsed = parseInt(traffic, 10);
    if (Number.isNaN(parsed) || parsed < 0) {
      setError('Paid traffic must be a non-negative whole number');
      return;
    }

    if (isSocial && !advertiserName.trim()) {
      setError('Enter the advertiser (Page) name — it is what identifies a social ad');
      return;
    }

    setSaving(true);
    setError('');
    try {
      // Correcting a logged find goes to the history endpoint. Posting to
      // /paid-traffic would append a second find rather than fix this one.
      const res = isEditingEntry
        ? await fetch(`${basePath}/api/paid-traffic/history/${targetEntryId}`, {
            method: 'PUT',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(
              isSocial
                ? {
                    country,
                    advertiser_name: advertiserName,
                    platforms: platforms.join(','),
                    ad_library_id: adLibraryId,
                    ad_status: adStatus,
                    ad_started_at: adStartedAt || null
                  }
                : { country, keyword }
            )
          })
        : await fetch(`${basePath}/api/paid-traffic/${row.id}/paid-traffic`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              traffic: parsed,
              channel,
              country,
              source,
              keyword,
              advertiser_name: advertiserName,
              platforms: platforms.join(','),
              ad_library_id: adLibraryId,
              ad_status: adStatus,
              ad_started_at: adStartedAt || null
            })
          });

      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || `Server error: ${res.status}`);

      // An entry edit returns the log row, not the affiliate — only hand the
      // parent a row when it actually got one, or the table would blank out.
      if (isEditingEntry) {
        onSaved(null, data.message);
        setTargetEntryId(null);
      } else {
        // The new entry's id comes back on data.history — send the screenshots
        // that were picked before it existed.
        await flushPendingImages(data.history?.id);
        onSaved(data.data, data.message);
      }
      loadHistory();
      setSource('manual');
    } catch (err) {
      setError(err.message || 'Failed to set paid traffic');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (entry) => {
    setEditingId(entry.id);
    setError('');
    setEditDraft({
      country: entry.country || '',
      keyword: entry.keyword || '',
      advertiser_name: entry.advertiser_name || ''
    });
  };

  const saveEdit = async (entry) => {
    setSavingEdit(true);
    setError('');
    try {
      // Only the half that applies to this entry's channel — sending the other
      // half would blank fields the operator was never shown.
      const isSocialEntry = entry.channel && entry.channel !== 'google';
      const payload = isSocialEntry
        ? { country: editDraft.country, advertiser_name: editDraft.advertiser_name }
        : { country: editDraft.country, keyword: editDraft.keyword };

      const res = await fetch(`${basePath}/api/paid-traffic/history/${entry.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || `Server error: ${res.status}`);

      setEditingId(null);
      loadHistory();
    } catch (err) {
      setError(err.message || 'Failed to update the entry');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleRemove = async () => {
    setRemoving(true);
    setError('');
    try {
      const res = await fetch(`${basePath}/api/paid-traffic/${row.id}/remove-paid-traffic`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(removeChannel ? { channel: removeChannel } : {})
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || `Server error: ${res.status}`);

      onSaved(data.data, `${data.message}. ${data.keywordsStripped} keyword(s) removed, ${data.keywordsKept} kept.`);
      setConfirmingRemove(false);
      setRemoveText('');
      setRemoveChannel('');
      if (data.trafficCleared) setTraffic('0');
      loadHistory();
    } catch (err) {
      setError(err.message || 'Failed to remove paid traffic');
      setConfirmingRemove(false);
    } finally {
      setRemoving(false);
    }
  };

  // What removing the selected channel will actually do, spelled out — the
  // consequences differ enough between "last channel" and "one of two" that a
  // single fixed warning would be wrong half the time.
  const removalConsequence = () => {
    const removing_ = removeChannel ? [removeChannel] : liveChannels;
    const remaining = liveChannels.filter((c) => !removing_.includes(c));

    if (remaining.length === 0) {
      return 'Paid traffic goes to 0 and the keywords your team added are stripped, so the Paid tag disappears across Recruitment, Directory and Untapped. SEMrush keywords and past history are kept.';
    }

    return `The ${describeChannels(removing_)} marker is removed, but the affiliate stays on paid traffic via ${describeChannels(remaining)} and keeps its current figure — there is one paid traffic number across all channels. Re-enter it if it should drop.`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-xl bg-white shadow-xl dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-slate-700">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-slate-100">
              <Zap className="h-5 w-5 text-amber-500" />
              Set paid traffic
            </h2>
            <p className="mt-0.5 font-mono text-xs text-gray-500 dark:text-slate-400">{row.affiliate_url}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          <div className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-600 dark:bg-slate-800 dark:text-slate-400">
            Current paid traffic:{' '}
            <span className="font-medium text-gray-900 dark:text-slate-100">{fmt(row.adwords_traffic)}</span>
            {liveChannels.length > 0 && (
              <span className="ml-2 text-xs">on {describeChannels(liveChannels)}</span>
            )}
          </div>

          {/* Where the ad was seen. First, because it decides which fields below
              are even asked for. */}
          <div>
            <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-slate-400">
              Where did you see the ad?
            </label>
            <div className="grid grid-cols-2 gap-2">
              {PAID_CHANNEL_LIST.map((option) => {
                const isActive = channel === option.key;
                return (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => {
                      setChannel(option.key);
                      if (isEditingEntry) clearTarget();
                    }}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left transition-colors ${
                      isActive
                        ? 'border-blue-500 bg-blue-50 dark:border-blue-500 dark:bg-blue-900/20'
                        : 'border-gray-200 hover:border-blue-300 dark:border-slate-700'
                    }`}
                  >
                    <span
                      className={`inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${option.solid}`}
                    >
                      {option.letter}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-gray-900 dark:text-slate-100">
                        {option.short}
                      </span>
                      <span className="block truncate text-[11px] text-gray-500 dark:text-slate-400">
                        {option.where}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Correct a find already logged on this channel, instead of typing
              it all again and leaving a duplicate. */}
          {editableFinds.length > 0 && (
            <div className="rounded-lg border border-gray-200 px-3 py-2 dark:border-slate-700">
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-slate-400">
                Saving as
              </label>
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={clearTarget}
                  className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                    !isEditingEntry
                      ? 'border-transparent bg-blue-600 text-white'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-blue-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300'
                  }`}
                >
                  New find
                </button>
                {editableFinds.map((find) => (
                  <button
                    key={find.id}
                    type="button"
                    onClick={() => loadEntryIntoForm(find)}
                    title="Load this find into the form and correct it in place"
                    className={`max-w-[220px] truncate rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                      targetEntryId === find.id
                        ? 'border-transparent bg-blue-600 text-white'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-blue-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300'
                    }`}
                  >
                    Edit: {find.advertiser_name || find.keyword || fmtDate(find.created_at)}
                  </button>
                ))}
              </div>
              {isEditingEntry && (
                <p className="mt-1.5 text-xs text-gray-500 dark:text-slate-400">
                  Correcting this find in place. The paid traffic figure is not changed — use New find to log a fresh
                  sighting.
                </p>
              )}
            </div>
          )}

          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-slate-400">
              Paid traffic
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                min={0}
                value={traffic}
                disabled={isInactiveAd || isEditingEntry}
                onChange={(e) => {
                  setTraffic(e.target.value);
                  setSource('manual');
                }}
                className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:disabled:bg-slate-800/60"
              />
              <button
                type="button"
                onClick={handleGenerate}
                disabled={isInactiveAd || isEditingEntry}
                title={`Generate a random value between ${GEN_MIN} and ${GEN_MAX}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-lg bg-amber-500 px-3 py-2 text-sm font-medium text-white hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Sparkles className="h-4 w-4" />
                Generate {GEN_MIN}–{GEN_MAX}
              </button>
            </div>
            {isInactiveAd ? (
              <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
                The ad is marked inactive, so paid traffic is left as it is. This is saved as a record that they were
                advertising — Recruitment and Directory will show it as past activity with the date and the advertiser.
              </p>
            ) : (
              <>
                {source === 'generated' && (
                  <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                    This value will be saved as generated, not as real SEMrush data.
                  </p>
                )}
                {liveChannels.length > 1 && (
                  <p className="mt-1 text-xs text-gray-400 dark:text-slate-500">
                    One figure covers every channel this affiliate advertises on.
                  </p>
                )}
              </>
            )}
          </div>

          {/* Evidence — swaps on the channel. */}
          {isSocial ? (
            <div className="space-y-4 rounded-lg border border-indigo-100 bg-indigo-50/50 p-3 dark:border-indigo-900/40 dark:bg-indigo-900/10">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-slate-400">
                    Advertiser (Page)
                  </label>
                  <input
                    type="text"
                    value={advertiserName}
                    onChange={(e) => setAdvertiserName(e.target.value)}
                    placeholder="Football Duke"
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-slate-400">
                    Library ID
                  </label>
                  <input
                    type="text"
                    value={adLibraryId}
                    onChange={(e) => setAdLibraryId(e.target.value)}
                    placeholder="1743975523468531"
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 font-mono text-sm text-gray-900 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                  />
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-slate-400">
                  Platforms
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {META_PLATFORMS.map((platform) => {
                    const isOn = platforms.includes(platform);
                    return (
                      <button
                        key={platform}
                        type="button"
                        onClick={() => togglePlatform(platform)}
                        className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                          isOn
                            ? 'border-transparent bg-indigo-600 text-white'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-indigo-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300'
                        }`}
                      >
                        {platform}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-slate-400">
                    Started running
                  </label>
                  <input
                    type="date"
                    value={adStartedAt}
                    onChange={(e) => setAdStartedAt(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-slate-400">
                    Ad status
                  </label>
                  <select
                    value={adStatus}
                    onChange={(e) => setAdStatus(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-slate-400">
                Keyword
              </label>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="best casino bonus"
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
              />
            </div>
          )}

          {/* Outside the channel switch: a sponsored result vanishes from the
              SERP when the campaign stops just as an ad leaves the Ad Library,
              so a Google find needs the same evidence a Meta find does. */}
          <AdScreenshotsField
            channel={channel}
            isEditingEntry={isEditingEntry}
            existingImages={existingImages}
            pendingImages={pendingImages}
            uploadingImages={uploadingImages}
            onUpload={uploadImages}
            onRemoveExisting={removeImage}
            onAddPending={addPendingImages}
            onRemovePending={removePendingImage}
          />

          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-slate-400">
              Country
            </label>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
            >
              <option value="">Select a country…</option>
              {/* Grouped so the markets used daily stay reachable without
                  scrolling past 139 alphabetical entries. */}
              <optgroup label="Main markets">
                {PRIORITY_MARKETS.map((c) => (
                  <option key={c.code} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </optgroup>
              <optgroup label="All other countries">
                {OTHER_MARKETS.map((c) => (
                  <option key={c.code} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </optgroup>
            </select>
          </div>
          <p className="text-xs text-gray-400 dark:text-slate-500">
            {isSocial
              ? 'The Ad Library country filter you were searching when you found this ad. Saved with this entry and shown on the dashboard.'
              : 'The keyword and country you were searching when you found this affiliate running paid ads. Saved with this entry and shown on the dashboard.'}
          </p>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800/50 dark:bg-red-900/20 dark:text-red-300">
              {error}
            </div>
          )}

          {/* History */}
          <div className="border-t border-gray-200 pt-4 dark:border-slate-700">
            <h3 className="mb-2 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-slate-400">
              <History className="h-3.5 w-3.5" />
              Paid traffic history
            </h3>

            {historyLoading ? (
              <div className="py-4 text-center text-sm text-gray-400 dark:text-slate-500">
                <Loader2 className="mx-auto h-4 w-4 animate-spin" />
              </div>
            ) : history.length === 0 ? (
              <p className="py-3 text-sm text-gray-400 dark:text-slate-500">
                No paid traffic has been set for this affiliate yet.
              </p>
            ) : (
              <ul className="max-h-56 space-y-2 overflow-y-auto">
                {history.map((h) => {
                  const entryChannel = channelMeta(h.channel);
                  return (
                    <li
                      key={h.id}
                      className="rounded-lg border border-gray-100 px-3 py-2 text-sm dark:border-slate-800"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-1.5 font-medium text-gray-900 dark:text-slate-100">
                          {entryChannel && (
                            <span
                              title={entryChannel.where}
                              className={`inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full text-[9px] font-bold ${entryChannel.solid}`}
                            >
                              {entryChannel.letter}
                            </span>
                          )}
                          {fmt(h.previous_traffic)}
                          <ArrowRight className="h-3 w-3 text-gray-400" />
                          <span className="text-emerald-600 dark:text-emerald-400">{fmt(h.new_traffic)}</span>
                        </span>
                        <span className="flex items-center gap-1.5">
                          <span
                            className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${
                              h.source === 'generated'
                                ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300'
                                : 'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-400'
                            }`}
                          >
                            {h.source}
                          </span>
                          {/* Corrects this entry in place. Saving the form above
                              would append a second find instead. */}
                          {h.source !== 'removal' && editingId !== h.id && (
                            <button
                              type="button"
                              onClick={() => startEdit(h)}
                              title="Correct this entry"
                              className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                            >
                              <Pencil className="h-3 w-3" />
                            </button>
                          )}
                        </span>
                      </div>
                      {editingId === h.id ? (
                        <div className="mt-2 space-y-2 rounded-lg bg-gray-50 p-2 dark:bg-slate-800/60">
                          <div>
                            <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-slate-400">
                              Country
                            </label>
                            <select
                              value={editDraft.country}
                              onChange={(e) => setEditDraft((d) => ({ ...d, country: e.target.value }))}
                              className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-900 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                            >
                              <option value="">No country</option>
                              <optgroup label="Main markets">
                                {PRIORITY_MARKETS.map((c) => (
                                  <option key={c.code} value={c.name}>{c.name}</option>
                                ))}
                              </optgroup>
                              <optgroup label="All other countries">
                                {OTHER_MARKETS.map((c) => (
                                  <option key={c.code} value={c.name}>{c.name}</option>
                                ))}
                              </optgroup>
                            </select>
                          </div>

                          <div>
                            <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-slate-400">
                              {h.channel && h.channel !== 'google' ? 'Advertiser (Page)' : 'Keyword'}
                            </label>
                            <input
                              type="text"
                              value={h.channel && h.channel !== 'google' ? editDraft.advertiser_name : editDraft.keyword}
                              onChange={(e) =>
                                setEditDraft((d) =>
                                  h.channel && h.channel !== 'google'
                                    ? { ...d, advertiser_name: e.target.value }
                                    : { ...d, keyword: e.target.value }
                                )
                              }
                              className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-900 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                            />
                          </div>

                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => saveEdit(h)}
                              disabled={savingEdit}
                              className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                            >
                              {savingEdit ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                              {savingEdit ? 'Saving…' : 'Save'}
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingId(null)}
                              className="rounded-lg px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 dark:text-slate-300 dark:hover:bg-slate-700"
                            >
                              Cancel
                            </button>
                            <span className="text-[10px] text-gray-400 dark:text-slate-500">
                              Corrects this entry — no new find is added
                            </span>
                          </div>
                        </div>
                      ) : (
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500 dark:text-slate-400">
                        {/* A social entry has no keyword — the Page is what was seen. */}
                        {h.advertiser_name && (
                          <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300">
                            {h.advertiser_name}
                          </span>
                        )}
                        {h.keyword && (
                          <span className="rounded bg-blue-50 px-1.5 py-0.5 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
                            {h.keyword}
                          </span>
                        )}
                        {h.platforms && <span className="truncate">{h.platforms}</span>}
                        <AdCreativeViewer images={h.ad_images} label={h.advertiser_name || h.keyword} compact />
                        {h.country && (
                          <span className="rounded bg-gray-100 px-1.5 py-0.5 dark:bg-slate-800">{h.country}</span>
                        )}
                        <span>{fmtDate(h.created_at)}</span>
                        {h.created_by && <span className="truncate">· {h.created_by}</span>}
                      </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* Stopped advertising — clears the value and the team's keywords. */}
        {confirmingRemove ? (
          <div className="border-t border-red-200 bg-red-50 px-5 py-4 dark:border-red-900/40 dark:bg-red-900/10">
            {/* Only worth asking which channel when there is more than one. */}
            {liveChannels.length > 1 && (
              <div className="mb-3">
                <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-red-700 dark:text-red-300">
                  Stopped advertising on
                </label>
                <select
                  value={removeChannel}
                  onChange={(e) => setRemoveChannel(e.target.value)}
                  className="w-full rounded-lg border border-red-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-red-500 dark:border-red-800/50 dark:bg-slate-800 dark:text-slate-100"
                >
                  <option value="">Every channel</option>
                  {liveChannels.map((key) => (
                    <option key={key} value={key}>
                      {channelMeta(key)?.label || key}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="mb-2 flex items-start gap-2 text-sm text-red-700 dark:text-red-300">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{removalConsequence()}</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="text"
                value={removeText}
                autoFocus
                onChange={(e) => setRemoveText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && removeText === 'REMOVE' && handleRemove()}
                placeholder="REMOVE"
                className="w-40 rounded-lg border border-red-200 bg-white px-3 py-2 font-mono text-sm text-gray-900 outline-none focus:border-red-500 dark:border-red-800/50 dark:bg-slate-800 dark:text-slate-100"
              />
              <button
                type="button"
                onClick={handleRemove}
                disabled={removeText !== 'REMOVE' || removing}
                className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {removing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />}
                {removing ? 'Removing…' : 'Confirm removal'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfirmingRemove(false);
                  setRemoveText('');
                  setRemoveChannel('');
                }}
                className="rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-white/60 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          Number(row.adwords_traffic) > 0 && (
            <div className="border-t border-gray-200 px-5 py-3 dark:border-slate-700">
              <button
                type="button"
                onClick={() => setConfirmingRemove(true)}
                className="flex items-center gap-2 text-sm text-red-600 hover:underline dark:text-red-400"
              >
                <Ban className="h-4 w-4" />
                This affiliate stopped running ads — remove it from paid traffic
              </button>
            </div>
          )
        )}

        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4 dark:border-slate-700">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? 'Saving…' : isEditingEntry ? 'Update find' : isInactiveAd ? 'Record past advertising' : 'Save paid traffic'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SetPaidTrafficModal;
